import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../pd-input/PdComboboxInput.js";
import "./PdSuggestionPanel.js";
import { LitElement, css, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { msg, str } from "@lit/localize";
//#region src/pd-suggestion-box/PdSuggestionBox.ts
/**
* Autocomplete/suggestion input with async search support.
*
* @tagname pd-suggestion-box
* @summary Typeahead input with async search and suggestion panel.
*
* @event element-selected - Fired when a suggestion is selected.
* @event element-unselected - Fired when selection is cleared.
*
* @cssprop --pd-input-field-width - Input and panel width. Default: `300px`.
* @cssprop --pd-input-field-height - Input height. Default: `2.2rem`.
* @cssprop --pd-input-field-bg-col - Input background. Default: `var(--pd-default-bg-col)`.
* @cssprop --pd-input-field-border - Input border. Default: `1px solid var(--pd-default-light-col)`.
* @cssprop --pd-input-field-border-bottom - Input bottom border. Default: `2px solid var(--pd-default-col)`.
* @cssprop --pd-radius-md - Input and panel border radius. Default: `0`.
* @cssprop --pd-input-label-col - Label color. Default: `var(--pd-default-font-title-col)`.
* @cssprop --pd-suggestion-panel-bg - Panel background. Default: `var(--pd-default-bg-col)`.
* @cssprop --pd-suggestion-panel-border - Panel border. Default: `1px solid var(--pd-default-light-col)`.
* @cssprop --pd-suggestion-panel-shadow - Panel shadow. Default: `var(--pd-shadow-lg)`.
* @cssprop --pd-suggestion-item-hover-bg - Item hover background. Default: `var(--pd-default-hover-col)`.
* @cssprop --pd-suggestion-item-name-color - Item name color. Default: `var(--pd-default-font-col)`.
* @cssprop --pd-suggestion-item-subinfo-color - Item subinfo color. Default: `var(--pd-default-font-muted-col)`.
*/
var PdSuggestionBox = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.callFunction = async (_searchInput) => {
			return Promise.resolve([]);
		};
		this.createNewEmptyItem = false;
		this.required = false;
		this.label = "";
		this.placeholder = "";
		this.delayTime = 800;
		this.minCharsToSearch = 2;
		this.maxResult = 100;
		this._selectedValue = "";
		this._currentSearchKey = "";
		this._loading = false;
		this._error = false;
		this._currentElements = [];
		this._activeIndex = -1;
	}
	static {
		this.styles = [css`
      :host {
        position: relative;
        --pd-input-field-width: 300px;
      }
    `];
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		if (this._timeoutRef) {
			window.clearTimeout(this._timeoutRef);
			this._timeoutRef = void 0;
		}
	}
	reset() {
		this._selectedValue = "";
		this._currentElements = [];
		this._currentSearchKey = "";
		this._localFilteredElements = void 0;
		this._loading = false;
		this._error = false;
		this._activeIndex = -1;
	}
	get _panelId() {
		return `${this.id || "suggestion"}-listbox`;
	}
	get _isPanelOpen() {
		return this._displayElements.length > 0 || this._loading;
	}
	get _displayElements() {
		return this._localFilteredElements || this._currentElements;
	}
	get _activeOptionId() {
		if (this._activeIndex >= 0 && this._activeIndex < this._displayElements.length) return `option-${this._displayElements[this._activeIndex].id}`;
	}
	render() {
		return html`
      <pd-combobox-input
        id="searchInputId"
        label="${this.label}"
        placeholder="${this.placeholder}"
        icon-right
        icon="searchIcon"
        initValue="${this._selectedValue}"
        ?expanded="${this._isPanelOpen}"
        ariaControls="${this._panelId}"
        ariaAutocomplete="list"
        ariaActivedescendant="${this._activeOptionId ?? ""}"
        @pd-form-element-change="${this._doChange}"
        @keydown="${this._handleKeydown}"
        ?required="${this.required}"
      ></pd-combobox-input>
      <pd-suggestion-panel
        id="${this._panelId}"
        ?loading="${this._loading}"
        .displayElements="${this._displayElements}"
        .activeIndex="${this._activeIndex}"
        @element-selected="${this._doSelection}"
      ></pd-suggestion-panel>
      ${this._error ? html`<p>
              ${msg("Technischer Fehler bei der Suche", { id: "pd.suggestion.box.error.txt" })}
            </p>` : nothing}
    `;
	}
	_doSelection(e) {
		if (this._timeoutRef) {
			window.clearTimeout(this._timeoutRef);
			this._timeoutRef = void 0;
		}
		const selectedEl = e.detail;
		this._selectedValue = selectedEl.name || selectedEl.label || selectedEl.id;
		this._closePanel();
	}
	_handleKeydown(e) {
		const elements = this._displayElements;
		switch (e.key) {
			case "ArrowDown":
				if (this._isPanelOpen && elements.length > 0) {
					e.preventDefault();
					this._activeIndex = Math.min(this._activeIndex + 1, elements.length - 1);
				}
				break;
			case "ArrowUp":
				if (this._isPanelOpen && elements.length > 0) {
					e.preventDefault();
					if (this._activeIndex <= 0) this._activeIndex = -1;
					else this._activeIndex = this._activeIndex - 1;
				}
				break;
			case "Enter":
				if (this._activeIndex >= 0 && this._activeIndex < elements.length) {
					e.preventDefault();
					const selectedEl = elements[this._activeIndex];
					this._selectedValue = selectedEl.name || selectedEl.id;
					this._closePanel();
					this.dispatchEvent(new CustomEvent("element-selected", {
						detail: selectedEl.objectValue || selectedEl,
						bubbles: true,
						composed: true
					}));
				}
				break;
			case "Escape": if (this._isPanelOpen) {
				e.preventDefault();
				this._closePanel();
			}
		}
	}
	_closePanel() {
		this._currentSearchKey = "";
		this._currentElements = [];
		this._localFilteredElements = void 0;
		this._activeIndex = -1;
	}
	_doChange(e) {
		if (this._selectedValue && e.detail.value === this._selectedValue) return;
		this._unselectElement();
		if (this._timeoutRef) window.clearTimeout(this._timeoutRef);
		this._activeIndex = -1;
		if (e.detail.value === "") {
			this._currentElements = [];
			this._currentSearchKey = "";
			this._localFilteredElements = void 0;
			this._loading = false;
			this._error = false;
		} else if (this._currentSearchKey !== "" && e.detail.value.startsWith(this._currentSearchKey)) {
			this._localFilteredElements = this._currentElements.filter((ce) => ce.name && ce.name.toLowerCase().replaceAll(" ", "").includes(e.detail.value.toLowerCase().replaceAll(" ", "")) || ce.subInfo && ce.subInfo.toLowerCase().includes(e.detail.value.toLowerCase()));
			if (this.createNewEmptyItem && this._localFilteredElements.length === 0) this._localFilteredElements = [this._createNewRecord(e)];
		} else if (e.detail.value.length >= this.minCharsToSearch) {
			this._loading = true;
			this._currentElements = [];
			this._localFilteredElements = void 0;
			this._timeoutRef = window.setTimeout(() => {
				this.callFunction(e.detail.value).then((result) => {
					this._currentSearchKey = e.detail.value;
					this._loading = false;
					this._localFilteredElements = void 0;
					this._currentElements = result;
					this._timeoutRef = void 0;
					if (this.createNewEmptyItem && this._currentElements.length === 0) this._currentElements = [this._createNewRecord(e)];
				}).catch((error) => {
					console.error("Error during suggestion search", error);
					this._loading = false;
					this._error = true;
					this._currentElements = [];
					this._localFilteredElements = void 0;
				});
			}, this.delayTime);
		}
	}
	_createNewRecord(e) {
		return {
			id: "NEW",
			name: msg(str`"${e.detail.value}" neu anlegen`, { id: "pd.suggestion.box.add.new" }),
			createNew: e.detail.value
		};
	}
	_unselectElement() {
		this.dispatchEvent(new CustomEvent("element-unselected", {
			bubbles: true,
			composed: true
		}));
	}
};
__decorate([property({ type: Object })], PdSuggestionBox.prototype, "callFunction", void 0);
__decorate([property({ type: Boolean })], PdSuggestionBox.prototype, "createNewEmptyItem", void 0);
__decorate([property({ type: Boolean })], PdSuggestionBox.prototype, "required", void 0);
__decorate([property({ type: String })], PdSuggestionBox.prototype, "label", void 0);
__decorate([property({ type: String })], PdSuggestionBox.prototype, "placeholder", void 0);
__decorate([property({ type: Number })], PdSuggestionBox.prototype, "delayTime", void 0);
__decorate([property({ type: Number })], PdSuggestionBox.prototype, "minCharsToSearch", void 0);
__decorate([property({ type: Number })], PdSuggestionBox.prototype, "maxResult", void 0);
__decorate([state()], PdSuggestionBox.prototype, "_selectedValue", void 0);
__decorate([state()], PdSuggestionBox.prototype, "_currentSearchKey", void 0);
__decorate([state()], PdSuggestionBox.prototype, "_loading", void 0);
__decorate([state()], PdSuggestionBox.prototype, "_error", void 0);
__decorate([state()], PdSuggestionBox.prototype, "_currentElements", void 0);
__decorate([state()], PdSuggestionBox.prototype, "_localFilteredElements", void 0);
__decorate([state()], PdSuggestionBox.prototype, "_activeIndex", void 0);
//#endregion
export { PdSuggestionBox };
